import { QuoteDetailChubbModel } from '@/cms/models/quote/detail';
import { QuoteFromSourceTypeEnum, QuotePlatformSourceEnum } from '@jove-frontend/enums';
import {
  OverrideActionStatus,
  PROVIDER_SOURCE,
  PolicyStatus,
  PolicyType,
  QuoteStatus,
  judgeCanHandlePartnerQuote,
  judgeIsJoveDirectSaleQuote,
  judgeIsJoveMasterAdmin,
  judgeIsJoveSalesAdmin,
  judgeIsPromotionQuote,
} from '@jove-frontend/uikit';
import { has, isBoolean, isEmpty, isObject, orderBy } from 'lodash';

export const formatPolicyType = (policyType: PolicyType) => {
  switch (policyType) {
    case PolicyType.ANNUAL_FLEXIBLE:
      return 'Annual Flexible';
    case PolicyType.CONTRACT_SPECIFIC:
      return 'Contract Specific';
  }
};

// get quote provider name
export const getPremiumProviderName = (providerStr: string) => {
  return PROVIDER_SOURCE()[providerStr]?.lowerLabel;
};

// quote data transform to server structor data
export const transformQuoteDetailToServerInfo = (model) => {
  const { turnoverRaw, employees, incomeOrigin, covers, isChubb, elSize } = model || {};
  if (turnoverRaw) model.turnoverRaw = Number(turnoverRaw);
  model.employees = Number(employees || 0);
  if (incomeOrigin) {
    for (const key of Object.keys(incomeOrigin)) {
      model.incomeOrigin[key] = Number(incomeOrigin[key]);
    }
  }
  if (covers) {
    const newCovers = {};
    for (const key of Object.keys(covers)) {
      const value = Number(covers[key]);
      if (value || value === 0) {
        // if chubb value is 0, don't pass value
        if (!(isChubb && value === 0)) {
          newCovers[key] = value;
        }
      }
    }
    model.covers = newCovers;
  }

  // Employees
  if (elSize) {
    elSize.clericalEmployees = elSize.clericalEmployees ?? 0;
    elSize.manualEmployees = elSize.manualEmployees ?? 0;
  }

  delete model.payload;
};

// quote data transform to server structor data && filter server data
export const filterQuoteDetailToServerInfo = (qupteDetailInfo) => {
  const { isChubb } = qupteDetailInfo || {};

  let model = null;
  if (isChubb) {
    model = new QuoteDetailChubbModel();
    model.serverDataConvertToModel(qupteDetailInfo);
  } else {
    model = qupteDetailInfo;
  }

  return model;
};

interface ValidateKeyProps {
  key: string;
  type: string;
}

interface GitUrlSortListItemProps {
  id: number;
  preId: number;
  suffixId: number[];
  url: string;
  validateKey: Array<ValidateKeyProps>;
  branchIdMap?: {
    [key: string]: number | string;
  };
}

const COFIRM_INFOMATION_PAGE = 'confirm-information';
const QUOTE_ASSUMPTION_CONFIRM_FIELDS = [
  {
    value: 'companyName',
    label: 'Company name',
  },
  {
    value: 'businessPostcode',
    label: 'Business postcode',
  },
  {
    value: 'vehicleList',
    label: 'Vehicle list',
  },
];

const companyAddressItem = {
  id: 3,
  preId: 2,
  suffixId: [4],
  url: 'company-address',
  validateKey: [
    { key: 'companyName', type: 'string' },
    { key: 'companyNumber', type: 'string' },
    { key: 'businessAddress', type: 'string' },
    { key: 'businessPostcode', type: 'string' },
    { key: 'businessCity', type: 'string' },
    { key: 'businessCountry', type: 'string' },
    { key: 'companyIncorporationDate', type: 'string' },
  ],
};
const tradingAddressItem = {
  id: 11,
  preId: 2,
  suffixId: [4],
  url: 'trading-address',
  validateKey: [
    { key: 'companyName', type: 'string' },
    { key: 'businessAddress2', type: 'string' },
    { key: 'businessPostcode', type: 'string' },
    { key: 'businessCity', type: 'string' },
    { key: 'companyIncorporationDate', type: 'string' },
    { key: 'birthDate', type: 'string' },
  ],
};
const gitLimitedCompanyUrlSortList: Array<GitUrlSortListItemProps> = [
  {
    id: 2,
    preId: 0,
    suffixId: [3, 11],
    url: 'business-type',
    validateKey: [
      {
        key: 'businessType',
        type: 'string',
      },
    ],
    branchIdMap: {
      key: 'businessType',
      'BT-001': 11,
      default: 3,
    },
  },
  {
    id: 4,
    preId: 3,
    suffixId: [5],
    url: 'annual-turnover',
    validateKey: [{ key: 'income', type: 'string' }],
  },
  {
    id: 5,
    preId: 4,
    suffixId: [6],
    url: 'vehicle-number',
    validateKey: [
      { key: 'git.vehicleRegistrationNumber', type: 'string' },
      { key: 'git.vehicleMake', type: 'string' },
      { key: 'git.vehicleModel', type: 'string' },
    ],
  },
  {
    id: 6,
    preId: 5,
    suffixId: [7],
    url: 'choose-bundle',
    validateKey: [{ key: 'git.bundle', type: 'string' }],
  },
  {
    id: 7,
    preId: 6,
    suffixId: [-1],
    url: 'declarations',
    validateKey: [{ key: 'git.declarations', type: 'boolean' }],
  },
];

// Determine the Jump url path to be forwarded based on the quote data
// only support good in transit
export const judgeQuotePageJumpUrlPath = (quoteData) => {
  const combineList = [...gitLimitedCompanyUrlSortList];
  if (quoteData.businessType === 'BT-002') {
    combineList.splice(1, 0, companyAddressItem);
  } else {
    combineList.splice(1, 0, tradingAddressItem);
  }

  // Determine which data is valid and complete
  let validaList = [];
  for (const item of combineList) {
    let valid = true;

    // validate item
    for (const formItem of item.validateKey) {
      let formValue = void 0;

      // Whether the value is recursive
      const keyList = formItem.key.split('.');
      for (const recursionKey of keyList) {
        formValue = isEmpty(formValue) ? quoteData[recursionKey] : formValue[recursionKey];
      }

      // Determine data type
      if (formItem.type === 'boolean') {
        if (!isBoolean(formValue)) {
          valid = false;
          break;
        }
      } else {
        if (!formValue) {
          valid = false;
          break;
        }
      }
    }
    if (valid) validaList.push(item);
  }

  validaList = orderBy(validaList, ['id'], ['asc']);

  if (validaList.length === 0) {
    // None of them passed the verification
    return combineList[0].url;
  } else {
    for (const item of validaList) {
      if (item.suffixId.includes(-1)) {
        // last page
        return COFIRM_INFOMATION_PAGE;
      } else {
        // If there are branches, the judgment is made
        let temp = void 0;
        let pageId = 0;
        if (item.branchIdMap) {
          const aValue = quoteData[item.branchIdMap.key];
          pageId = item.branchIdMap[aValue] || item.branchIdMap.default;
          temp = validaList.find((aItem) => aItem.id === pageId);
        } else {
          temp = validaList.find((aItem) => item.suffixId.includes(aItem.id));
          pageId = item.suffixId[0];
        }

        if (!temp) {
          const nextItem = combineList.find((aItem) => pageId === aItem.id);
          return nextItem?.url || COFIRM_INFOMATION_PAGE;
        }
      }
    }
  }
};

export const getQuoteAssumptionConfirmFieldList = (assumptionFields) => {
  if (!isObject(assumptionFields)) {
    return [];
  }

  return QUOTE_ASSUMPTION_CONFIRM_FIELDS.filter((item) => has(assumptionFields, item.value));
};

export const shouldConfirmQuoteAssumption = (assumptionFields) => {
  return getQuoteAssumptionConfirmFieldList(assumptionFields).length > 0;
};

export const canEditQuoteData = (status) => {
  return ![
    QuoteStatus.PURCHASED,
    QuoteStatus.PAYMENT_PENDING,
    QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED,
    QuoteStatus.CANCELLED,
    QuoteStatus.PAYMENT_PROCESSING,
    QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS,
    QuoteStatus.DROPOUT_AFTER_SIGNING,
    QuoteStatus.PRE_PAYMENT,
  ].includes(status);
};

export const canSaveQuote = (status, config?: { source?: string }) => {
  return (
    ![
      QuoteStatus.PURCHASED,
      QuoteStatus.NOT_INTERESTED,
      QuoteStatus.CANCELLED,
      QuoteStatus.PAYMENT_PENDING,
      QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED,
      QuoteStatus.PAYMENT_PROCESSING,
      QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS,
      QuoteStatus.DROPOUT_AFTER_SIGNING,
      QuoteStatus.PRE_PAYMENT,
    ].includes(status) && config?.source !== 'Acturis'
  );
};

export const canSendQuote = (status, providerCode?: string) => {
  return canSaveQuote(status) && (![QuoteStatus.REFERRAL].includes(status) || PROVIDER_SOURCE().ACCELERANT.value.includes(providerCode));
};

export const canViewQuote = (status) => {
  return canSaveQuote(status);
};

export const canSetQuotePrice = (status) => {
  return canEditQuoteData(status);
};

export const canModifyQuotePrice = (status) => {
  return ![
    QuoteStatus.PAYMENT_PENDING,
    QuoteStatus.CANCELLED,
    QuoteStatus.PURCHASE_FAILED,
    QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED,
    QuoteStatus.PURCHASED,
    QuoteStatus.PAYMENT_PROCESSING,
    QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS,
    QuoteStatus.DROPOUT_AFTER_SIGNING,
    QuoteStatus.PRE_PAYMENT,
  ].includes(status);
};

export const canRepairQuote = (status) => {
  return [
    QuoteStatus.PURCHASED,
    QuoteStatus.PAYMENT_PENDING,
    QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED,
    QuoteStatus.PURCHASE_FAILED,
    QuoteStatus.PAYMENT_PROCESSING,
    QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS,
    QuoteStatus.DROPOUT_AFTER_SIGNING,
    QuoteStatus.PRE_PAYMENT,
  ].includes(status);
};

export const canSetNotInteresedQuote = (status) => {
  return canSaveQuote(status);
};

export const canDeclineQuote = (status) => {
  return [
    QuoteStatus.QUOTE_START,
    QuoteStatus.DROPOUT_BEFORE_QUOTE,
    QuoteStatus.DROPOUT_BEFORE_PURCHASE,
    QuoteStatus.DROPOUT_BEFORE_SIGNING,
    QuoteStatus.QUOTED,
    QuoteStatus.NOT_STARTED,
  ].includes(status);
};

export const canDirectlyPaymentQuote = (status) => {
  return ![
    QuoteStatus.DECLINED,
    QuoteStatus.NOT_INTERESTED,
    QuoteStatus.PRE_PAYMENT,
    QuoteStatus.CANCELLED,
    QuoteStatus.PURCHASED,
    QuoteStatus.PAYMENT_PROCESSING,
    QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS,
    QuoteStatus.BROKER,
  ].includes(status);
};

export const canManualGenerateQuoteSchedule = (status, config?: { source?: string }) => {
  return [
    QuoteStatus.QUOTED,
    QuoteStatus.PAYMENT_PENDING,
    QuoteStatus.PAYMENT_PROCESSING,
    QuoteStatus.DROPOUT_BEFORE_PURCHASE,
    QuoteStatus.DROPOUT_BEFORE_SIGNING,
    QuoteStatus.DROPOUT_AFTER_SIGNING,
    QuoteStatus.PRE_PAYMENT,
    QuoteStatus.REFERRAL,
  ].includes(status);
};

export const canAssignJoveSales = (status, config?: { source?: string }) => {
  return ![
    QuoteStatus.PURCHASED,
    QuoteStatus.NOT_INTERESTED,
    QuoteStatus.CANCELLED,
    QuoteStatus.PAYMENT_PENDING,
    QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED,
    QuoteStatus.PAYMENT_PROCESSING,
    QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS,
    QuoteStatus.PRE_PAYMENT,
  ].includes(status);
};

const judgeSupportPremiumOverridePolicyStatus = (status) => {
  return [
    PolicyStatus.POLICY_UPDATE_QUOTE,
    PolicyStatus.POLICY_RENEWED_UPDATE_QUOTE,
    PolicyStatus.POLICY_RENEWED_INITIATED,
    PolicyStatus.POLICY_UPDATE_INITIATED,
    PolicyStatus.POLICY_UPDATE_REFERRAL,
    PolicyStatus.POLICY_RENEWED_UPDATE_REFERRAL,
  ].includes(status);
};

export const judgeMasterCanDirectEditOverridePremium = (config?: {
  handleSourceType: QuoteFromSourceTypeEnum;
  policyDetailInfo?: any;
  quoteDetailInfo?: any;
  platformSourceType?: QuotePlatformSourceEnum;
  userInfo?: any;
}) => {
  const { handleSourceType, policyDetailInfo, quoteDetailInfo, platformSourceType, userInfo } = config || {};
  if (handleSourceType === QuoteFromSourceTypeEnum.policy) {
    const { status, providerCode, quotePayload } = policyDetailInfo || {};
    const { promotionInfo } = quotePayload || {};

    const isPromotionFlag =
      judgeIsPromotionQuote(promotionInfo) && platformSourceType !== QuotePlatformSourceEnum.portal && judgeIsJoveMasterAdmin(userInfo);
    const isJoveMasterAdminFlag = judgeIsJoveDirectSaleQuote(quotePayload) && judgeIsJoveMasterAdmin(userInfo);

    return (
      judgeSupportPremiumOverridePolicyStatus(status) &&
      [PROVIDER_SOURCE().ACCELERANT.value, PROVIDER_SOURCE().ACCELERANT_GIT.value, PROVIDER_SOURCE().RECRUITMENT.value].includes(providerCode) &&
      (isPromotionFlag || isJoveMasterAdminFlag)
    );
  } else {
    const { status, providerCode, overrideActionStatus, promotionInfo } = quoteDetailInfo || {};
    const isPromotionFlag =
      judgeIsPromotionQuote(promotionInfo) && platformSourceType !== QuotePlatformSourceEnum.portal && judgeIsJoveMasterAdmin(userInfo);
    const isJoveMasterAdminFlag = judgeIsJoveDirectSaleQuote(quoteDetailInfo) && judgeIsJoveMasterAdmin(userInfo);
    return (
      platformSourceType === QuotePlatformSourceEnum.admin &&
      canSaveQuote(status) &&
      [PROVIDER_SOURCE().ACCELERANT.value, PROVIDER_SOURCE().ACCELERANT_GIT.value, PROVIDER_SOURCE().RECRUITMENT.value].includes(providerCode) &&
      overrideActionStatus !== OverrideActionStatus.PENDING &&
      (isPromotionFlag || isJoveMasterAdminFlag)
    );
  }
};

export const judgeCanSubmitProposedOverridePremium = (config?: {
  handleSourceType: QuoteFromSourceTypeEnum;
  policyDetailInfo?: any;
  quoteDetailInfo?: any;
  platformSourceType?: QuotePlatformSourceEnum;
  quotePriceInfo?: any;
  userInfo?: any;
  extraRelatedInfo?: any;
}) => {
  const { handleSourceType, policyDetailInfo, quoteDetailInfo, platformSourceType, quotePriceInfo, userInfo, extraRelatedInfo } = config || {};
  if (handleSourceType === QuoteFromSourceTypeEnum.policy) {
    const { status, providerCode, quotePayload } = policyDetailInfo || {};
    const { promotionInfo } = quotePayload || {};

    const isPromotionFlag = judgeIsPromotionQuote(promotionInfo) && platformSourceType === QuotePlatformSourceEnum.portal;
    const isJoveDirectSaleFlag = judgeIsJoveDirectSaleQuote(quotePayload) && judgeIsJoveSalesAdmin(userInfo);

    return (
      !!quotePriceInfo?.price &&
      judgeSupportPremiumOverridePolicyStatus(status) &&
      [PROVIDER_SOURCE().ACCELERANT.value, PROVIDER_SOURCE().ACCELERANT_GIT.value, PROVIDER_SOURCE().RECRUITMENT.value].includes(providerCode) &&
      (isPromotionFlag || isJoveDirectSaleFlag)
    );
  } else {
    const { status, providerCode, promotionInfo } = quoteDetailInfo || {};
    const isPromotionFlag =
      platformSourceType === QuotePlatformSourceEnum.portal &&
      judgeCanHandlePartnerQuote({ promotionInfo, platformSourceType, tripartiteInfo: extraRelatedInfo?.tripartiteInfo });
    const isJoveDirectSaleFlag = judgeIsJoveDirectSaleQuote(quoteDetailInfo) && judgeIsJoveSalesAdmin(userInfo);

    return (
      !!quotePriceInfo?.price &&
      canEditQuoteData(status) &&
      ![QuoteStatus.REFERRAL].includes(status) &&
      [PROVIDER_SOURCE().ACCELERANT.value, PROVIDER_SOURCE().ACCELERANT_GIT.value, PROVIDER_SOURCE().RECRUITMENT.value].includes(providerCode) &&
      (isPromotionFlag || isJoveDirectSaleFlag)
    );
  }
};

export const judgeCanReviewProposedOverridePremium = (config?: {
  handleSourceType: QuoteFromSourceTypeEnum;
  policyDetailInfo?: any;
  quoteDetailInfo?: any;
  platformSourceType?: QuotePlatformSourceEnum;
  userInfo?: any;
}) => {
  const { handleSourceType, policyDetailInfo, quoteDetailInfo, platformSourceType, userInfo } = config || {};
  const judgeQuoteDetailInfo = handleSourceType === QuoteFromSourceTypeEnum.policy ? policyDetailInfo?.quotePayload : quoteDetailInfo;
  const { overrideActionStatus, promotionInfo } = judgeQuoteDetailInfo || {};

  const isPromotionFlag = judgeIsPromotionQuote(promotionInfo) && platformSourceType !== QuotePlatformSourceEnum.portal;
  const isJoveDirectSaleFlag = judgeIsJoveDirectSaleQuote(quoteDetailInfo) && judgeIsJoveMasterAdmin(userInfo);

  return overrideActionStatus === OverrideActionStatus.PENDING && (isPromotionFlag || isJoveDirectSaleFlag);
};

export const judgeCanWaitProposedOverridePremiumApproval = (config?: {
  handleSourceType: QuoteFromSourceTypeEnum;
  policyDetailInfo?: any;
  quoteDetailInfo?: any;
  platformSourceType?: QuotePlatformSourceEnum;
  userInfo?: any;
}) => {
  const { handleSourceType, policyDetailInfo, quoteDetailInfo, platformSourceType, userInfo } = config || {};
  const judgeQuoteDetailInfo = handleSourceType === QuoteFromSourceTypeEnum.policy ? policyDetailInfo?.quotePayload : quoteDetailInfo;
  const { overrideActionStatus, promotionInfo } = judgeQuoteDetailInfo || {};

  const isPromotionFlag = judgeIsPromotionQuote(promotionInfo) && platformSourceType === QuotePlatformSourceEnum.portal;
  const isJoveDirectSaleFlag = judgeIsJoveDirectSaleQuote(judgeQuoteDetailInfo) && judgeIsJoveSalesAdmin(userInfo);

  return overrideActionStatus === OverrideActionStatus.PENDING && (isPromotionFlag || isJoveDirectSaleFlag);
};

export const canShowQuotePayPlan = (quoteDetails) => {
  const { isCustomizedActivity, source } = quoteDetails || {};

  return source !== 'Acturis' && !isCustomizedActivity;
};
